import React, { useMemo, useState } from "react";
import { Drawer, Typography, Tag, Input, Empty, Badge } from "antd";
import { SearchOutlined, ThunderboltOutlined } from "@ant-design/icons";

import {
  SAMPLE_CATEGORIES,
  SAMPLE_TEMPLATES,
  type SampleCategory,
  type SampleTemplate,
} from "./data/sampleEmailTemplates.data";

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (template: SampleTemplate) => void;
  initialCategory?: string;
}

const matchesQuery = (tpl: SampleTemplate, q: string): boolean => {
  if (!q) return true;
  const n = q.trim().toLowerCase();
  return (
    tpl.name.toLowerCase().includes(n) ||
    tpl.template_code.toLowerCase().includes(n) ||
    tpl.description.toLowerCase().includes(n) ||
    tpl.subject.toLowerCase().includes(n)
  );
};

const SampleTemplatesPicker: React.FC<Props> = ({
  open,
  onClose,
  onSelect,
  initialCategory,
}) => {
  const [activeCategory, setActiveCategory] = useState<string>(
    initialCategory && initialCategory !== "all" ? initialCategory : "all",
  );
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: SAMPLE_TEMPLATES.length };
    SAMPLE_TEMPLATES.forEach((tpl) => {
      map[tpl.category] = (map[tpl.category] || 0) + 1;
    });
    return map;
  }, []);

  const filtered = useMemo(
    () =>
      SAMPLE_TEMPLATES.filter(
        (tpl) =>
          (activeCategory === "all" || tpl.category === activeCategory) &&
          matchesQuery(tpl, search),
      ),
    [activeCategory, search],
  );

  const visibleCategories = useMemo<SampleCategory[]>(
    () =>
      SAMPLE_CATEGORIES.filter(
        (c) => c.value === "all" || (counts[c.value] || 0) > 0,
      ),
    [counts],
  );

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ThunderboltOutlined style={{ color: "#1677ff" }} />
          <span style={{ fontWeight: 600 }}>Starter templates</span>
          <Badge
            count={SAMPLE_TEMPLATES.length}
            style={{
              backgroundColor: "#eef2ff",
              color: "#4338ca",
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </div>
      }
      placement="right"
      width={560}
      open={open}
      onClose={onClose}
      zIndex={1100}
      styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          background: "#fafbfc",
          flexShrink: 0,
        }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          placeholder="Search by name, code, subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginTop: 12,
          }}
        >
          {visibleCategories.map((cat) => {
            const isActive = cat.value === activeCategory;
            const count = counts[cat.value] || 0;
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => setActiveCategory(cat.value)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 20,
                  border: `1px solid ${isActive ? cat.color : "#e5e7eb"}`,
                  background: isActive ? `${cat.color}14` : "#fff",
                  color: isActive ? cat.color : "#374151",
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                  cursor: "pointer",
                }}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
                <span
                  style={{
                    fontSize: 10,
                    background: isActive ? cat.color : "#f3f4f6",
                    color: isActive ? "#fff" : "#6b7280",
                    padding: "1px 6px",
                    borderRadius: 10,
                    minWidth: 18,
                    textAlign: "center",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {filtered.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                No templates match your filter
              </Text>
            }
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {filtered.map((tpl) => (
              <SampleCard
                key={tpl.id}
                template={tpl}
                onSelect={() => {
                  onSelect(tpl);
                  onClose();
                }}
              />
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

const SampleCard: React.FC<{
  template: SampleTemplate;
  onSelect: () => void;
}> = ({ template: tpl, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const cat = SAMPLE_CATEGORIES.find((c) => c.value === tpl.category);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
      style={{
        border: `1px solid ${hovered ? tpl.accent : "#eef0f3"}`,
        borderRadius: 10,
        background: "#fff",
        cursor: "pointer",
        transition: "all 0.18s",
        boxShadow: hovered
          ? `0 6px 18px -8px ${tpl.accent}66`
          : "0 1px 2px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <div style={{ height: 4, width: "100%", background: tpl.accent }} />
      <div style={{ padding: "12px 14px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: `${tpl.accent}1a`,
                color: tpl.accent,
                fontSize: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {tpl.icon || "✉️"}
            </div>
            <Text strong style={{ fontSize: 13.5 }}>
              {tpl.name}
            </Text>
          </div>
          {cat ? (
            <Tag
              style={{
                margin: 0,
                fontSize: 10,
                background: `${cat.color}14`,
                border: `1px solid ${cat.color}33`,
                color: cat.color,
              }}
            >
              {cat.icon} {cat.label}
            </Tag>
          ) : null}
        </div>

        <Paragraph
          type="secondary"
          style={{ margin: "6px 0 8px", fontSize: 12, lineHeight: 1.5 }}
        >
          {tpl.description}
        </Paragraph>

        <div
          style={{
            background: "#f8fafc",
            borderRadius: 6,
            padding: "6px 8px",
            fontSize: 11,
            color: "#475569",
          }}
        >
          <strong style={{ marginRight: 4 }}>Subject:</strong>
          {tpl.subject}
        </div>

        {hovered && (
          <div
            style={{
              marginTop: 10,
              padding: "6px 10px",
              background: tpl.accent,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              textAlign: "center",
            }}
          >
            Use this template →
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleTemplatesPicker;
